import React, { createContext, useContext, useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { listCharacters } from '../graphql/queries';
import { createCharacter, updateCharacter, deleteCharacter as deleteCharacterMutation } from '../graphql/mutations';

const client = generateClient();

const CharacterContext = createContext({ authMode: 'userPool' });

export const useCharacter = () => useContext(CharacterContext);

export const CharacterProvider = ({ children }) => {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCharacters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const currentUser = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();
      const session = await fetchAuthSession();
      
      console.log('Current user:', currentUser);
      console.log('User attributes:', userAttributes);
      console.log('Session:', session);

      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const charactersData = await client.graphql({
        query: listCharacters
      });

      if (charactersData.errors) {
        throw new Error(charactersData.errors[0].message);
      }

      setCharacters(charactersData.data.listCharacters.items);
    } catch (err) {
      console.error('Error fetching characters:', err);
      if (err.message.includes('Unauthorized')) {
        setError('You are not authorized to view characters. Please check your permissions or try logging out and back in.');
      } else {
        setError(err.message || 'An error occurred while fetching characters');
      }
      setCharacters([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const addCharacter = async (characterData) => {
    setLoading(true);
    setError(null);
    try {
      const newCharacter = await client.graphql({
        query: createCharacter,
        variables: { input: characterData },
        authMode: 'AMAZON_COGNITO_USER_POOLS'
      });

      if (newCharacter.errors) {
        throw new Error(newCharacter.errors[0].message);
      }

      setCharacters(prevCharacters => [...prevCharacters, newCharacter.data.createCharacter]);
      return newCharacter.data.createCharacter;
    } catch (err) {
      console.error('Error adding character:', err);
      setError(err.message || 'An error occurred while adding the character');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCharacterData = async (id, characterData) => {
    setLoading(true);
    setError(null);
    try {
      const updatedCharacter = await client.graphql({
        query: updateCharacter,
        variables: { input: { id, ...characterData } },
        authMode: 'AMAZON_COGNITO_USER_POOLS'
      });

      if (updatedCharacter.errors) {
        throw new Error(updatedCharacter.errors[0].message);
      }

      setCharacters(prevCharacters => 
        prevCharacters.map(char => char.id === id ? updatedCharacter.data.updateCharacter : char)
      );
      return updatedCharacter.data.updateCharacter;
    } catch (err) {
      console.error('Error updating character:', err);
      setError(err.message || 'An error occurred while updating the character');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteCharacter = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const deletedCharacter = await client.graphql({
        query: deleteCharacterMutation,
        variables: { input: { id } }
      });

      if (deletedCharacter.errors) {
        throw new Error(deletedCharacter.errors[0].message);
      }

      setCharacters(prevCharacters => prevCharacters.filter(char => char.id !== id));
      return deletedCharacter.data.deleteCharacter;
    } catch (err) {
      console.error('Error deleting character:', err);
      setError(err.message || 'An error occurred while deleting the character');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <CharacterContext.Provider value={{
      characters,
      loading,
      error,
      fetchCharacters,
      addCharacter,
      updateCharacterData,
      deleteCharacter
    }}>
      {children}
    </CharacterContext.Provider>
  );
};

export default CharacterProvider;